import { DatePipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DoCheck,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
    computed,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MedpaceAdditionalDetailsModalComponent } from '@app/admin/manage/modals/additional-details-modal.component';
import { MedpaceDeleteRequestModalComponent } from '@app/admin/manage/modals/delete-request-modal.component';
import { MedpaceRequestErrorModalComponent } from '@app/admin/manage/modals/request-error-modal.component';
import { MedpaceViewHistoryModalComponent } from '@app/admin/manage/modals/view-history-modal.component';
import { RequestTypes } from '@app/enums/RequestTypes';
import { TravelType } from '@app/enums/travel-type.enum';
import { ChangeRequestStatusRequest, GetRequestEventLogResponse, RequestControllerv2Service } from '@app/generated-api';
import {
    DisplayErrorDialogComponent,
    DisplayErrorDialogData,
} from '@app/v2/components/dialogs/display-error-dialog/display-error-dialog.component';
import { MessageDialogComponent } from '@app/v2/components/dialogs/message-dialog/message-dialog.component';
import {
    PaymentRequestStatusChangeModalComponent,
    PaymentRequestStatusChangeModalComponentData,
    PaymentRequestStatusChangeModalComponentResult,
} from '@app/v2/components/dialogs/request-status-change-modal/payment/payment-request-status-change-modal.component';
import {
    TravelRequestStatusChangeModalComponent,
    TravelRequestStatusChangeModalComponentData,
    TravelRequestStatusChangeModalComponentResult,
} from '@app/v2/components/dialogs/request-status-change-modal/travel/travel-request-status-change-modal.component';
import { NoteCardContext } from '@app/v2/components/request-side-notes/note-card-context.enum';
import { ModalResult } from '@app/v2/enums/modal-result.enum';
import { RequestType } from '@app/v2/enums/request-types.enum';
import { ReimbursementRequestService } from '@app/v2/services/reimbursement-request/reimbursement-request.service';
import { StipendRequestService } from '@app/v2/services/stipend-request/stipend-request.service';
import * as MedpaceCreateRequestVisitDetailsComponent from '@components/molecules/forms/medpace-create-request-visit-details/medpace-create-request-visit-details.component';
import * as MedpaceEditRequestVisitDetailsComponent from '@components/molecules/forms/medpace-edit-request-visit-details/medpace-edit-request-visit-details.component';
import {
    PaymentRequestCardComponent,
    PaymentRequestCardFormGroup,
} from '@components/request/payment-request-card/payment-request-card.component';
import {
    CaregiverFlightFormGroup,
    SingleFlightFormGroup,
} from '@components/request/travel-request-card/form-groups/detail-form-groups/travel/flight-request.formgroup';
import { CaregiverTrainTripFormGroup } from '@components/request/travel-request-card/form-groups/detail-form-groups/travel/train-request.formgroup';
import {
    BookableCaregiver,
    CheckboxFormGroup,
    TravelRequestCardFormGroup,
} from '@components/request/travel-request-card/form-groups/travel-request-card.formgroup';
import { TravelCardComponent } from '@components/request/travel-request-card/travel-request-card.component';
import { MdsOption } from '@medpacesoftwaredevelopment/designsystem';
import { CountryViewModel } from '@models/country';
import { CurrencyViewModel } from '@models/currency';
import { EmittedEvent } from '@models/event';
import { InputChange } from '@models/event-objects/input-change';
import { CreateGetThereUrlViewModel } from '@models/getthere/createGetThereUrlViewModel';
import { GetThereInputParameters } from '@models/getthere/getThereInputParameters';
import { Caregiver, Patient, getCaregiverFullName } from '@models/patient';
import {
    GenericRequest,
    GenericRequestStatus,
    PaymentRequestStatus,
    Reimbursement,
    RequestStatus,
    TrainTravelDetails,
    TrainTravelDetailsFormModel,
    TrainTravelType,
    TravelAccommodations,
    TravelCardViewModel,
    TravelRequestCaregiver,
    TravelRequestFlightTravelDetail,
    TravelRequestStatus,
    VisitDetails,
    mapTrainTravelFormModelToTrainTravelDetails,
} from '@models/request';
import { Site, SiteServices } from '@models/site';
import { Study, StudyExpense, StudyTravelOption, VisitName } from '@models/study';
import { TransferData } from '@models/transferData';
import { VisitGroup } from '@models/visitGroup';
import { AdminRequestServices } from '@services/admin/admin-request.service';
import { AttachmentSizeService } from '@services/attachment-size/attachment-size.service';
import { EventService } from '@services/event/event.service';
import { GetThereService, GetThereTravelerId, mapToDTO } from '@services/getthere/getthere.service';
import { HistoryDataService } from '@services/historyData/history-data.service';
import { LoadingViewTriggerService } from '@services/loading-module-trigger/loading-module-trigger.service';
import { AllPatientStatusesEnum } from '@services/patient-status/patient-status.service';
import { Step } from '@services/scroll/steps-interface';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { CountryStateService } from '@services/state-management/country-state.service';
import { CurrencyStateService } from '@services/state-management/currency-state.service';
import { PatientStateService } from '@services/state-management/patient-state.service';
import { RequestStateService } from '@services/state-management/request-state.service';
import { SiteStateService } from '@services/state-management/site-state.service';
import { StudyStateService } from '@services/state-management/study-state.service';
import { TimeOptionsStateService } from '@services/state-management/time-options-state.service';
import { TravelViewModelService } from '@services/travel-view-model/travel-view-model.service';
import { ModalSize, getModalConfig } from '@utility/modal-helper';
import { PersistentFormGroup } from '@utility/persistent-forms';
import { FormGroupValueOf, MdsOptionGeneric, markFormGroupTouched } from '@utility/utility';
import { sha256 } from 'hash.js';
import { Moment } from 'moment';
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    Subject,
    combineLatest,
    filter,
    forkJoin,
    map,
    merge,
    of,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs';
import { maxSizeOfFilesInMB } from 'src/globals';

@Component({
    selector: 'medpace-request',
    standalone: false,
    templateUrl: './request.component.html',
    styleUrls: ['./request.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None,
})
export class MedpaceRequestComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
    private readonly travelViewModelService = inject(TravelViewModelService);
    private readonly requestServices = inject(AdminRequestServices);
    private readonly router = inject(Router);
    private readonly attachmentSizeService = inject(AttachmentSizeService);
    private readonly loadingViewTriggerService = inject(LoadingViewTriggerService);
    private readonly snackbarService = inject(SnackbarService);
    protected readonly requestStateService = inject(RequestStateService);
    private readonly studyStateService = inject(StudyStateService);
    private readonly siteStateService = inject(SiteStateService);
    private readonly patientStateService = inject(PatientStateService);
    private readonly activeRoute = inject(ActivatedRoute);
    private readonly datePipe = inject(DatePipe);
    private readonly eventService = inject(EventService);
    private readonly countryStateService = inject(CountryStateService);
    private readonly currencyStateService = inject(CurrencyStateService);
    private readonly timeOptionsStateService = inject(TimeOptionsStateService);
    private readonly fb = inject(FormBuilder);
    private readonly historyDataService = inject(HistoryDataService);
    private readonly getThereService = inject(GetThereService);
    private readonly generatedRequestService = inject(RequestControllerv2Service);
    private readonly dialog = inject(MatDialog);

    public GenericRequestStatus = GenericRequestStatus;
    @Input()
    elevatedAccess: boolean;

    patient: Patient;
    patient$: Observable<Patient> = this.patientStateService.getSummaryPatientData();
    patientNotes = toSignal(this.patientStateService.patientNotes.asObservable(), { initialValue: [] });

    patientId$: Observable<number> = this.patient$.pipe(
        map((patient) => patient?.id),
        filter((id) => id !== null && id !== undefined)
    );

    study: Study;
    study$: Observable<Study> = this.studyStateService.getStudy().pipe(
        map((study) => {
            if (study?.studyInfo?.expenseTypes)
                return <Study>{
                    ...study,
                    studyInfo: {
                        ...study.studyInfo,
                        expenseTypes: study?.studyInfo?.expenseTypes?.filter((x) => x.active),
                    },
                };

            return study;
        })
    );

    site: Site;
    site$: Observable<Site> = this.siteStateService.getSite();
    siteNotes = toSignal(this.siteStateService.siteNotes.asObservable(), { initialValue: [] });

    siteId$: Observable<number> = this.site$.pipe(
        map((site) => site?.id),
        filter((id) => id !== null && id !== undefined)
    );

    siteServices: SiteServices;

    visitTypes: VisitName[];
    visitTypes$: Observable<VisitName[]> = this.requestStateService.getVisits();

    expenseTypes: MdsOption[];

    request: GenericRequest;
    request$: Observable<GenericRequest> = this.requestStateService.getRequest();

    requestNotes = toSignal(this.requestStateService.getRequest().pipe(map((request) => request?.requestNotes ?? [])), {
        initialValue: [],
    });

    @Input()
    isUpdate: boolean;

    @Input()
    stipendOrOOP: string;

    @Output()
    submitClickEvent = new EventEmitter();

    @Output()
    incrementalSave = new EventEmitter();

    @Output()
    saveClickEvent = new EventEmitter();

    @Output()
    statusChangeEvent = new EventEmitter<ChangeRequestStatusRequest>();

    @Output()
    requestChangeEvent = new EventEmitter<GenericRequest>();

    @Output()
    patientNavigationRequestEvent = new EventEmitter<InputChange>();

    isButtonSubmitActive = signal<boolean>(true);

    showTravelCards: boolean = false;
    showPaymentCard: boolean = false;
    travelAvailable: boolean;
    reimbursementAvailable: boolean;
    stipendAvailable: boolean;
    paymentAvailable: boolean;
    bannerExpanded: boolean = false;
    isCompletable: boolean;
    requestStatusLog: any[] = [];
    validationMessage: string = `Please complete the following:`;
    validForm: boolean = true;
    actualStatus: string = 'Loading...';
    maxSummaryFileSizesInMB: number;
    private componentDestroyed$: Subject<boolean> = new Subject();
    genericRequestStatus = GenericRequestStatus;
    requestSignal = signal<GenericRequest>(null);

    private requestTypeStep: Step = {
        name: 'Request Information',
        isActive: true,
        identifier: 'requestType',
    };
    private studyInformationStep: Step = {
        name: 'Study Details',
        isActive: false,
        identifier: 'studyInformation',
    };
    private patientInformationStep: Step = {
        name: 'Patient Information',
        isActive: false,
        identifier: 'patientInformation',
    };
    private visitDetailsStep: Step = {
        name: 'Visit Details',
        isActive: false,
        identifier: 'visitDetails',
    };
    private travelStep: Step = {
        name: 'Travel Information',
        isActive: false,
        identifier: 'travel-information-step',
    };

    private paymentStep: Step = {
        name: 'Payment Information',
        isActive: false,
        identifier: 'paymentInformation',
    };
    steps: Step[] = [this.requestTypeStep, this.studyInformationStep, this.patientInformationStep];

    defaultsteps: Step[] = this.steps;

    requestFormGroup: FormGroup<{
        travelCard: TravelRequestCardFormGroup;
        VisitDetails: AbstractControl;
        paymentCard?: PaymentRequestCardFormGroup;
    }>;
    isNewRequest: boolean = true;

    protected readonly NoteCardContext = NoteCardContext;

    getRequestNoteCardContext(requestType: RequestTypes): NoteCardContext {
        if (requestType === RequestTypes.TRAVEL) return NoteCardContext.Travel;

        const paymentType = this.requestFormGroup.controls.paymentCard?.controls.type.value;
        return paymentType === RequestTypes.STIPEND ? NoteCardContext.Stipend : NoteCardContext.Reimbursement;
    }

    private requestTypeSubject = new ReplaySubject<RequestTypes>();
    public requestType$ = combineLatest([
        this.requestTypeSubject.pipe(startWith(null)),
        this.activeRoute.queryParamMap,
    ]).pipe(
        map(([requestTypeSubjectValue, queryParamMap], index) => {
            let requestType: RequestTypes = null;
            if (index === 0 && queryParamMap.has('payment-travel')) {
                const paymentTravel = queryParamMap.get('payment-travel');
                if (paymentTravel === 'payment') requestType = RequestTypes.PAYMENT;
                else requestType = RequestTypes.TRAVEL;
            } else requestType = requestTypeSubjectValue;
            return requestType;
        }),
        filter((type) => !!type),
        tap((requestType: RequestTypes) => {
            //only one of controls travelCard, ExpenseDetails should be enabled and valid for travel request to save
            if (requestType === RequestTypes.TRAVEL) {
                //disable ExpenseDetails
                //enable travelCard
                this.requestFormGroup.controls.paymentCard.disable();
                this.requestFormGroup.controls.travelCard.enable();
            } else if (requestType === RequestTypes.PAYMENT) {
                //enable ExpenseDetails
                //disable travelCard
                this.requestFormGroup.controls.paymentCard.enable();
                this.requestFormGroup.controls.travelCard.disable();
            }
        }),
        shareReplay()
    );
    public createRequestVisitDetails_ViewModel$ = combineLatest([
        this.site$,
        this.visitTypes$,
        this.requestType$,
        this.patient$,
    ]).pipe(
        filter(([site, visitNames, requestType, patient]) => !!site && !!visitNames && !!requestType && !!patient),
        map(([site, visitNames, requestType, patient]) => {
            return {
                site,
                visitNames,
                requestType,
                patient,
            };
        }),
        map((result) => {
            const visitNameOptions = result.visitNames
                .filter((vn) => vn.visitGroup.id === result.patient.patientIdentification.visitGroupId)
                .map((name) => ({ value: name, viewValue: name.name }));

            return <MedpaceCreateRequestVisitDetailsComponent.ViewModel>{
                requestType: result.requestType,
                paymentRequest_ViewModel: <MedpaceCreateRequestVisitDetailsComponent.PaymentRequest_ViewModel>{
                    siteAddress: result.site.address,
                    siteNumber: result.site.info.siteNumber,
                    visitNameOptions: visitNameOptions,
                },
                travelRequest_ViewModel: <MedpaceCreateRequestVisitDetailsComponent.TravelRequest_ViewModel>{
                    timePickerOptions: undefined, // will now be passed by medpace-edit-request-visit-details component
                    siteAddress: result.site.address,
                    siteNumber: result.site.info.siteNumber,
                    visitNameOptions: visitNameOptions,
                },
            };
        })
    );

    private createRequestVisitDetailsSubject =
        new BehaviorSubject<MedpaceCreateRequestVisitDetailsComponent.MedpaceCreateRequestVisitDetailsComponent>(null);
    @ViewChild(MedpaceCreateRequestVisitDetailsComponent.MedpaceCreateRequestVisitDetailsComponent)
    public set createRequestVisitDetails_Component(
        value: MedpaceCreateRequestVisitDetailsComponent.MedpaceCreateRequestVisitDetailsComponent
    ) {
        if (value) {
            this.createRequestVisitDetailsSubject.next(value);
            this.requestFormGroup.setControl('VisitDetails', value.formGroup);
        }
    }

    public editRequestVisitDetails_ViewModel$ = combineLatest([
        this.request$,
        this.site$,
        this.visitTypes$,
        this.patient$,
    ]).pipe(
        filter(([request, site, visitNames, patient]) => !!request && !!site && !!visitNames && !!patient),
        map(([request, site, visitNames, patient]) => {
            const requestType = request.type === 'payment' ? RequestTypes.PAYMENT : RequestTypes.TRAVEL;

            const filteredVisitNames = visitNames.filter(
                (vn) => vn.visitGroup.id === patient.patientIdentification.visitGroupId
            );
            const visitNamesCopy = [...filteredVisitNames];
            // In PatientPACE, the patient might select a special value for Visit Name, "Not sure", represented as NULL in database.
            // In this only special case, we want such value to be available in a Visit Name dropdown so PCS users can edit and save existing request without error.
            // Otherwise, the "Not sure" option should not be visible to PCS users.
            if (request.visitDetails.visitNameId === undefined) {
                const unspecifiedVisitName: VisitName = <VisitName>{
                    id: undefined,
                    name: 'Unspecified',
                    scheduled: false,
                    sortOrder: 0,
                    studyId: 0,
                    visitGroup: <VisitGroup>{ name: '', sortOrder: 0 },
                };
                visitNamesCopy.push(unspecifiedVisitName);
            }

            const visitNameOptions = visitNamesCopy.map(
                (name) => <MdsOptionGeneric<VisitName>>{ value: name, viewValue: name.name }
            );

            const visitName = visitNamesCopy.find((name) => name.id === request.visitDetails.visitNameId);
            let viewModel = <MedpaceEditRequestVisitDetailsComponent.ViewModel>{
                requestType: requestType,
                paymentRequest_ViewModel: null,
                travelRequest_ViewModel: null,
            };
            if (requestType === RequestTypes.PAYMENT) {
                viewModel.paymentRequest_ViewModel = <MedpaceEditRequestVisitDetailsComponent.PaymentRequest_ViewModel>{
                    siteAddress: site.address,
                    siteNumber: site.info.siteNumber,
                    visitNameOptions: visitNameOptions,
                    visitName: visitName,
                    visitDate: request.visitDetails.visitStartDate,
                };
            } else if (requestType === RequestTypes.TRAVEL) {
                const visitStartDateTime = request.visitDetails.visitStartDate;
                const visitEndDateTime = request.visitDetails.visitEndDate;
                const [startHours, startMinutes] = request.visitDetails.visitStartTime.split(':');
                const [endHours, endMinutes] = request.visitDetails.visitEndTime.split(':');
                visitStartDateTime?.hour(Number(startHours));
                visitStartDateTime?.minute(Number(startMinutes));
                visitEndDateTime?.hour(Number(endHours));
                visitEndDateTime?.minute(Number(endMinutes));

                viewModel.travelRequest_ViewModel = <MedpaceEditRequestVisitDetailsComponent.TravelRequest_ViewModel>{
                    timePickerOptions: undefined,
                    siteAddress: site.address,
                    siteNumber: site.info.siteNumber,
                    specialNavigationInstructions: request.visitDetails.travelerInstructions,
                    visitNameOptions: visitNameOptions,
                    visitName: visitName,
                    visitStartDateTime: visitStartDateTime,
                    visitEndDateTime: visitEndDateTime,
                };
            }

            return viewModel;
        })
    );
    private editRequestVisitDetailsSubject =
        new BehaviorSubject<MedpaceEditRequestVisitDetailsComponent.MedpaceEditRequestVisitDetailsComponent>(null);
    @ViewChild(MedpaceEditRequestVisitDetailsComponent.MedpaceEditRequestVisitDetailsComponent)
    public set editRequestVisitDetails_Component(
        value: MedpaceEditRequestVisitDetailsComponent.MedpaceEditRequestVisitDetailsComponent
    ) {
        if (value) {
            this.editRequestVisitDetailsSubject.next(value);
            this.requestFormGroup.setControl('VisitDetails', value.formGroup);
        }
    }

    studyTravelOptions: StudyTravelOption[];

    private newTravelCard = new Subject<void>();
    protected isUpdate$ = this.activeRoute.data.pipe(map((data) => data['isUpdate'] as boolean));
    protected travelCardVM$: Observable<TravelCardViewModel> = combineLatest({
        requestType: this.requestType$,
        isUpdate: this.isUpdate$,
        request: this.requestStateService.getRequest(),
        patient: this.patientStateService.getPatient(),
        countries: this.countryStateService.getCountries(),
        currencies: this.currencyStateService.getCurrencies(),
        timeOptions: this.timeOptionsStateService.getTimeOptions(),
    }).pipe(
        filter((result) => result.requestType === RequestTypes.TRAVEL),
        filter((result) => !!result.patient && !!result.request),
        switchMap((result) =>
            forkJoin({
                carService: this.historyDataService.getPatientTravelRequestHistoryForCarService(result.patient.id),
                flight: this.historyDataService.getPatientTravelRequestHistoryForFlight(result.patient.id),
                lodging: this.historyDataService.getPatientTravelRequestHistoryForLodging(result.patient.id),
                other: this.historyDataService.getPatientTravelRequestHistoryForOther(result.patient.id),
                carRental: this.historyDataService.getPatientTravelRequestHistoryForRentalCar(result.patient.id),
                train: this.historyDataService.getPatientTravelRequestHistoryForTrain(result.patient.id),
            }).pipe(
                map((historyData) => {
                    return [result, historyData] as const;
                })
            )
        ),
        tap(([result, _]) => {
            let primaryCaregiver = result.patient.caregivers.find((caregiver) => caregiver.isPrimary);
            const otherCaregivers = result.patient.caregivers.filter((caregiver) => !caregiver.isPrimary);
            const patientTravelDetails = result.request.travelRequests;
            const travellingCaregivers = result.patient.caregivers.map((caregiver) => {
                const travelRequestCaregiver =
                    result.request.travelRequestCaregivers?.find((c) => c.caregiverId === caregiver.id) ||
                    <TravelRequestCaregiver>{
                        caregiverId: caregiver.id,
                        caregiverName: getCaregiverFullName(caregiver),
                        caregiver: caregiver,
                        flightRequestTheSameAsPatient: true,
                        isChecked: false,
                        isPrimary: caregiver.isPrimary,
                        travelRequestId: result.request.id,
                        flightRequestDetails: {
                            airportOrigin: '',
                            airportDestination: '',
                            returnDate: null,
                            departureDate: null,
                            specialRequests: '',
                            travelType: TravelType.NO_FLIGHT,
                            id: null, //id: 0,
                        },
                        trainTravelDetails: {
                            isSameAsPatient: true,
                            trainFormInLegacyMode: false,
                            trainStationOrigin: '',
                            trainStationDestination: '',
                            departureDate: null,
                            returnDate: null,
                            trainTravelType: TrainTravelType.NoTravel,
                            specialRequests: '',
                        },
                    };
                return { travelRequestCaregiver, caregiver };
            });

            const fallbackValueForFlightDetails = {
                // before August 2023, we only had one text field for the unstructured information, `Special Requests`.
                specialRequests: patientTravelDetails.flightRequests,

                travelType: TravelType.ROUND_TRIP,

                airportOrigin: '',
                airportDestination: '',
                departureDate: null,
                returnDate: null,
                id: null,
            };

            const patientFlightRequestDetails: TravelRequestFlightTravelDetail =
                patientTravelDetails.flightRequestDetails || fallbackValueForFlightDetails;
            const caregiversFlightRequestDetails = travellingCaregivers.map<FormGroupValueOf<CaregiverFlightFormGroup>>(
                (value) => {
                    const flightRequestDetails = value.travelRequestCaregiver.flightRequestDetails || {
                        travelType: TravelType.NO_FLIGHT,
                        airportOrigin: '',
                        airportDestination: '',
                        departureDate: null,
                        returnDate: null,
                        specialRequests: '',
                        id: null, //id: 0,
                    };
                    let travelType = <TravelType>flightRequestDetails.travelType;
                    if (travelType === null || travelType === undefined) travelType = TravelType.NO_FLIGHT; // travelType needs to be specified, otherwise and error will be thrown. Currently if caregiver is not checked the response from server will contain null
                    const needsAirTravel = travelType !== TravelType.NO_FLIGHT;
                    const details: FormGroupValueOf<CaregiverFlightFormGroup['controls']['details']> = needsAirTravel
                        ? {
                              bookIdenticalTrip: value.travelRequestCaregiver.flightRequestTheSameAsPatient,
                              flightDetails: flightRequestDetails,
                          }
                        : {
                              bookIdenticalTrip: false,
                              flightDetails: flightRequestDetails,
                          };
                    return {
                        needsAirTravel: needsAirTravel,
                        caregiver: value.caregiver,
                        details: details,
                    };
                }
            );
            const patientTrainTripRequestDetails: TrainTravelDetails =
                patientTravelDetails.trainTravelDetailsForPatient || {
                    trainTravelType: TrainTravelType.RoundTrip,
                    trainStationOrigin: '',
                    trainStationDestination: '',
                    departureDate: null,
                    returnDate: null,
                    isSameAsPatient: true,
                    specialRequests: '',
                    trainFormInLegacyMode: false,
                };

            const travelPreferences = result.patient.travelPreferences;
            const flightPreferences = {
                needsTravel: travelPreferences.airtravel.needsAirTravel,
                airlinePreference1: travelPreferences.airtravel.airlinePreference1,
                airlinePreference2: travelPreferences.airtravel.airlinePreference2,
                frequentFlyerNumber1: travelPreferences.airtravel.frequentFlyer1,
                frequentFlyerNumber2: travelPreferences.airtravel.frequentFlyer2,
                knownTravelerNumber: travelPreferences.airtravel.knownTravelerNumber,
                seatPreference: travelPreferences.airtravel.seatPreference,
                specialNeeds: travelPreferences.airtravel.airlineSpecialNeeds,
            };
            const trainPreferences = {
                specialNeeds: travelPreferences.traintravel.specialNeeds,
            };
            const lodgingPreferences = {
                hotelBrandPreference: travelPreferences.lodging.hotelBrandPreference,
                roomPreference: travelPreferences.lodging.lodgingRoomPreference,
                specialNeeds: travelPreferences.lodging.specialRequirements,
                allergies: travelPreferences.lodging.allergies,
            };
            const carRentalPreferences = {
                frequentTravelerNumber: travelPreferences.rentalCar.rentalCarFrequentTravelerNumber,
            };
            const transferPreferences = {
                specialNeeds: travelPreferences.groundTransportation.groundSpecialNeeds,
            };
            if (result.isUpdate) {
                const primaryCaregiverFlightDetails = caregiversFlightRequestDetails.find(
                    (details) => details.caregiver.id === primaryCaregiver?.id
                );
                this.requestFormGroup.setControl(
                    'travelCard',
                    TravelRequestCardFormGroup.create({
                        accompanyingCaregivers: {
                            primaryCaregiver: {
                                checked: result.request.travelRequestCaregivers.some(
                                    (travelRequestCaregiver) =>
                                        travelRequestCaregiver.caregiverId === primaryCaregiver?.id
                                ),
                                value: {
                                    caregiver: primaryCaregiver,
                                    bookable:
                                        !!primaryCaregiverFlightDetails &&
                                        (primaryCaregiverFlightDetails.details.bookIdenticalTrip ||
                                            primaryCaregiverFlightDetails.details.flightDetails.travelType !==
                                                TravelType.NO_FLIGHT),
                                },
                            },
                            otherCaregivers: otherCaregivers.map<
                                FormGroupValueOf<CheckboxFormGroup<BookableCaregiver>>
                            >((caregiver) => {
                                const caregiverFlightDetails = caregiversFlightRequestDetails.find(
                                    (details) => details.caregiver.id === caregiver.id
                                );
                                return {
                                    checked: result.request.travelRequestCaregivers.some(
                                        (travelRequestCaregiver) => travelRequestCaregiver.caregiverId === caregiver.id
                                    ),
                                    value: {
                                        caregiver: caregiver,
                                        bookable:
                                            !!caregiverFlightDetails &&
                                            (caregiverFlightDetails.details.bookIdenticalTrip ||
                                                caregiverFlightDetails.details.flightDetails.travelType !==
                                                    TravelType.NO_FLIGHT),
                                    },
                                };
                            }),
                            none: {
                                checked: result.request.travelRequestCaregivers.length === 0,
                                value: null,
                            },
                        },
                        travelDetails: {
                            flight: {
                                needsTravel: patientTravelDetails.needsAirTravel,
                                travel: {
                                    preferences: flightPreferences,
                                    requestDetails: {
                                        patientFlightDetails: {
                                            travelType: patientFlightRequestDetails.travelType,
                                            airportOrigin: patientFlightRequestDetails.airportOrigin,

                                            airportDestination: patientFlightRequestDetails.airportDestination,

                                            departureDate: patientFlightRequestDetails.departureDate,
                                            returnDate: patientFlightRequestDetails.returnDate,
                                            specialRequests: patientFlightRequestDetails.specialRequests,
                                            id: patientFlightRequestDetails.id,
                                        },
                                        caregivers: caregiversFlightRequestDetails,
                                    },
                                },
                            },
                            train: {
                                needsTravel: patientTravelDetails.needsTrainTravel,
                                travel: {
                                    preferences: trainPreferences,
                                    requestDetails: {
                                        patientTrainTripDetails: {
                                            travelType: patientTrainTripRequestDetails.trainTravelType,
                                            stationOrigin: patientTrainTripRequestDetails.trainStationOrigin,
                                            stationDestination: patientTrainTripRequestDetails.trainStationDestination,
                                            departureDate: patientTrainTripRequestDetails.departureDate,
                                            returnDate: patientTrainTripRequestDetails.returnDate,
                                            specialRequests: patientTrainTripRequestDetails.specialRequests,
                                        },
                                        caregivers: travellingCaregivers.map<
                                            FormGroupValueOf<CaregiverTrainTripFormGroup>
                                        >((value) => {
                                            let travelType = <TrainTravelType>(
                                                value.travelRequestCaregiver.trainTravelDetails?.trainTravelType
                                            );
                                            if (travelType === null || travelType === undefined)
                                                travelType = TrainTravelType.NoTravel; // travelType needs to be specified, otherwise and error will be thrown. Currently if caregiver is not checked the response from server will contain null

                                            const needsTrainTravel = travelType !== TrainTravelType.NoTravel;
                                            let details: FormGroupValueOf<
                                                CaregiverTrainTripFormGroup['controls']['details']
                                            > = needsTrainTravel
                                                ? {
                                                      bookIdenticalTrip:
                                                          value.travelRequestCaregiver.trainTravelDetails
                                                              ?.isSameAsPatient,
                                                      trainDetails: {
                                                          travelType: travelType,
                                                          stationOrigin:
                                                              value.travelRequestCaregiver.trainTravelDetails
                                                                  ?.trainStationOrigin,
                                                          stationDestination:
                                                              value.travelRequestCaregiver.trainTravelDetails
                                                                  ?.trainStationDestination,
                                                          departureDate:
                                                              value.travelRequestCaregiver.trainTravelDetails
                                                                  ?.departureDate,
                                                          returnDate:
                                                              value.travelRequestCaregiver.trainTravelDetails
                                                                  ?.returnDate,
                                                          specialRequests:
                                                              value.travelRequestCaregiver.trainTravelDetails
                                                                  ?.specialRequests,
                                                      },
                                                  }
                                                : {
                                                      bookIdenticalTrip: false,
                                                      trainDetails: {
                                                          travelType: travelType,
                                                          stationOrigin: '',
                                                          stationDestination: '',
                                                          departureDate: null,
                                                          returnDate: null,
                                                          specialRequests: '',
                                                      },
                                                  };
                                            return {
                                                needsTrainTravel: needsTrainTravel,
                                                caregiver: value.caregiver,
                                                details: details,
                                            };
                                        }),
                                    },
                                },
                            },
                            lodging: {
                                needsTravel: patientTravelDetails.needsLodging,
                                travel: {
                                    preferences: lodgingPreferences,
                                    requestDetails: {
                                        checkInDate: patientTravelDetails.lodgingCheckInDate,
                                        checkOutDate: patientTravelDetails.lodgingCheckOutDate,
                                        specialRequests: patientTravelDetails.lodgingRequests,
                                    },
                                },
                            },
                            rentalCar: {
                                needsTravel: patientTravelDetails.needsRentalCar,
                                travel: {
                                    preferences: carRentalPreferences,
                                    requestDetails: {
                                        pickupAddress: patientTravelDetails.rentalCarLocation,
                                        pickupCountry: result.countries.find(
                                            (option) => option.countryCode === patientTravelDetails.rentalCarCountry
                                        )?.viewValue,
                                        useFlightDate: patientTravelDetails.rentalCarUseFlightDate,
                                        pickupDate: patientTravelDetails.rentalCarPickUpDate,
                                        pickupTime: !patientTravelDetails.rentalCarPickUpTime
                                            ? null
                                            : result.timeOptions.find(
                                                  (option) =>
                                                      option.value ===
                                                      this.timeOptionsStateService.convertTimeStringToMinutesSinceMidnight(
                                                          patientTravelDetails.rentalCarPickUpTime
                                                      )
                                              )?.value,
                                        dropOffDate: patientTravelDetails.rentalCarDropOffDate,
                                        dropOffTime: !patientTravelDetails.rentalCarDropOffTime
                                            ? null
                                            : result.timeOptions.find(
                                                  (option) =>
                                                      option.value ===
                                                      this.timeOptionsStateService.convertTimeStringToMinutesSinceMidnight(
                                                          patientTravelDetails.rentalCarDropOffTime
                                                      )
                                              )?.value,
                                        specialRequests: patientTravelDetails.rentalCarRequests,
                                    },
                                },
                            },
                            carService: {
                                needsTravel: patientTravelDetails.needsCar,
                                travel: {
                                    preferences: transferPreferences,
                                    requestDetails: {
                                        transfers: patientTravelDetails.transferDatas.map((transferData) => {
                                            return {
                                                ...transferData,
                                                pickupDate: transferData.pickupDateTime?.clone().startOf('day'),
                                                pickupTime: transferData.pickupDateTime
                                                    ?.clone()
                                                    .diff(
                                                        transferData.pickupDateTime.clone().startOf('day'),
                                                        'minutes'
                                                    ),
                                            };
                                        }),
                                        specialRequests: patientTravelDetails.carServiceRequests,
                                    },
                                },
                            },
                            other: {
                                needsTravel: patientTravelDetails.needsOther,
                                travel: {
                                    requestDetails: {
                                        specialRequests: patientTravelDetails.otherRequests,
                                    },
                                },
                            },
                        },
                        receipts: [],
                    })
                );
            } else {
                this.requestFormGroup.setControl(
                    'travelCard',
                    TravelRequestCardFormGroup.create({
                        accompanyingCaregivers: {
                            primaryCaregiver: {
                                checked: false,
                                value: {
                                    caregiver: primaryCaregiver,
                                    bookable: false,
                                },
                            },
                            otherCaregivers: otherCaregivers.map<
                                FormGroupValueOf<CheckboxFormGroup<BookableCaregiver>>
                            >((caregiver) => {
                                return {
                                    checked: false,
                                    value: {
                                        caregiver: caregiver,
                                        bookable: false,
                                    },
                                };
                            }),
                            none: {
                                checked: false,
                                value: null,
                            },
                        },
                        travelDetails: {
                            flight: {
                                needsTravel: false,
                                travel: {
                                    preferences: flightPreferences,
                                    requestDetails: {
                                        patientFlightDetails: {
                                            travelType: TravelType.ROUND_TRIP,
                                            airportOrigin: '',
                                            airportDestination: '',
                                            departureDate: null,
                                            returnDate: null,
                                            specialRequests: '',
                                            id: null, // 0,
                                        },
                                        caregivers: travellingCaregivers.map<
                                            FormGroupValueOf<CaregiverFlightFormGroup>
                                        >((value) => {
                                            return {
                                                needsAirTravel: false,
                                                caregiver: value.caregiver,
                                                details: {
                                                    bookIdenticalTrip: true,
                                                    flightDetails: {
                                                        travelType: TravelType.NO_FLIGHT,
                                                        airportOrigin: '',
                                                        airportDestination: '',
                                                        departureDate: null,
                                                        returnDate: null,
                                                        specialRequests: '',
                                                        id: null, //  id: 0,
                                                    },
                                                },
                                            };
                                        }),
                                    },
                                },
                            },
                            train: {
                                needsTravel: false,
                                travel: {
                                    preferences: trainPreferences,
                                    requestDetails: {
                                        patientTrainTripDetails: {
                                            travelType: TrainTravelType.RoundTrip,
                                            stationOrigin: '',
                                            stationDestination: '',
                                            departureDate: null,
                                            returnDate: null,
                                            specialRequests: '',
                                        },
                                        caregivers: travellingCaregivers.map<
                                            FormGroupValueOf<CaregiverTrainTripFormGroup>
                                        >((value) => {
                                            return {
                                                needsTrainTravel: false,
                                                caregiver: value.caregiver,
                                                details: {
                                                    bookIdenticalTrip: true,
                                                    trainDetails: {
                                                        travelType: TrainTravelType.NoTravel,
                                                        stationOrigin: '',
                                                        stationDestination: '',
                                                        departureDate: null,
                                                        returnDate: null,
                                                        specialRequests: '',
                                                    },
                                                },
                                            };
                                        }),
                                    },
                                },
                            },
                            lodging: {
                                needsTravel: false,
                                travel: {
                                    preferences: lodgingPreferences,
                                    requestDetails: {
                                        checkInDate: null,
                                        checkOutDate: null,
                                        specialRequests: '',
                                    },
                                },
                            },
                            rentalCar: {
                                needsTravel: false,
                                travel: {
                                    preferences: carRentalPreferences,
                                    requestDetails: {
                                        pickupAddress: '',
                                        pickupCountry: null,
                                        useFlightDate: true,
                                        pickupDate: null,
                                        pickupTime: null,
                                        dropOffDate: null,
                                        dropOffTime: null,
                                        specialRequests: '',
                                    },
                                },
                            },
                            carService: {
                                needsTravel: false,
                                travel: {
                                    preferences: transferPreferences,
                                    requestDetails: {
                                        transfers: [],
                                        specialRequests: '',
                                    },
                                },
                            },
                            other: {
                                needsTravel: false,
                                travel: {
                                    requestDetails: {
                                        specialRequests: '',
                                    },
                                },
                            },
                        },
                        receipts: [],
                    })
                );
            }
            this.newTravelCard.next();

            this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.flight.controls.travel.controls.requestDetails.controls.caregivers.controls.forEach(
                (caregiverControl) =>
                    combineLatest({
                        bookIdenticalTrip: caregiverControl.controls.details.controls.bookIdenticalTrip.valueChanges,
                        travelType:
                            caregiverControl.controls.details.controls.flightDetails.controls.travelType.valueChanges,
                    })
                        .pipe(
                            takeUntil(this.newTravelCard),
                            tap((result) => {
                                const needsAirTravel =
                                    result.bookIdenticalTrip || result.travelType !== TravelType.NO_FLIGHT;
                                const relatedControl = [
                                    this.requestFormGroup.controls.travelCard.controls.accompanyingCaregivers.controls
                                        .primaryCaregiver,
                                    ...this.requestFormGroup.controls.travelCard.controls.accompanyingCaregivers
                                        .controls.otherCaregivers.controls,
                                ].find(
                                    (control) =>
                                        control.getRawValue().value.caregiver.id ===
                                        caregiverControl.getRawValue().caregiver.id
                                );
                                if (relatedControl) {
                                    let caregiverValue = relatedControl.controls.value.getRawValue();
                                    caregiverValue.bookable = needsAirTravel;
                                    relatedControl.controls.value.setValue(caregiverValue, { emitEvent: false });
                                }
                            })
                        )
                        .subscribe()
            );

            //group observable that enables/disables formcontrols returned from it
            const formValue = this.requestFormGroup.getRawValue();
            merge(
                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.flight.controls.needsTravel.valueChanges.pipe(
                    takeUntil(this.newTravelCard),
                    startWith(formValue.travelCard.travelDetails.flight.needsTravel),
                    map((value) => {
                        return {
                            value,
                            controls: <PersistentFormGroup[]>[
                                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.flight
                                    .controls.travel.controls.requestDetails,
                                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.flight
                                    .controls.travel.controls.preferences,
                            ],
                        };
                    })
                ),
                ...this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.flight.controls.travel.controls.requestDetails.controls.caregivers.controls.map(
                    (c) =>
                        c.controls.details.controls.bookIdenticalTrip.valueChanges.pipe(
                            takeUntil(this.newTravelCard),
                            startWith(c.getRawValue().details.bookIdenticalTrip),
                            map((bookIdenticalTrip) => {
                                return {
                                    value: !bookIdenticalTrip,
                                    controls: <PersistentFormGroup[]>[c.controls.details.controls.flightDetails],
                                };
                            })
                        )
                ),
                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.train.controls.needsTravel.valueChanges.pipe(
                    takeUntil(this.newTravelCard),
                    startWith(formValue.travelCard.travelDetails.train.needsTravel),
                    map((value) => {
                        return {
                            value,
                            controls: <PersistentFormGroup[]>[
                                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.train.controls
                                    .travel.controls.requestDetails,
                                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.train.controls
                                    .travel.controls.preferences,
                            ],
                        };
                    })
                ),
                ...this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.train.controls.travel.controls.requestDetails.controls.caregivers.controls.map(
                    (c) =>
                        c.controls.details.controls.bookIdenticalTrip.valueChanges.pipe(
                            takeUntil(this.newTravelCard),
                            startWith(c.getRawValue().details.bookIdenticalTrip),
                            map((bookIdenticalTrip) => {
                                return {
                                    value: !bookIdenticalTrip,
                                    controls: <PersistentFormGroup[]>[c.controls.details.controls.trainDetails],
                                };
                            })
                        )
                ),
                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.lodging.controls.needsTravel.valueChanges.pipe(
                    takeUntil(this.newTravelCard),
                    startWith(formValue.travelCard.travelDetails.lodging.needsTravel),
                    map((value) => {
                        return {
                            value,
                            controls: <PersistentFormGroup[]>[
                                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.lodging
                                    .controls.travel.controls.requestDetails,
                                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.lodging
                                    .controls.travel.controls.preferences,
                            ],
                        };
                    })
                ),
                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.rentalCar.controls.needsTravel.valueChanges.pipe(
                    takeUntil(this.newTravelCard),
                    startWith(formValue.travelCard.travelDetails.rentalCar.needsTravel),
                    map((value) => {
                        return {
                            value,
                            controls: <PersistentFormGroup[]>[
                                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.rentalCar
                                    .controls.travel.controls.requestDetails,
                                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.rentalCar
                                    .controls.travel.controls.preferences,
                            ],
                        };
                    })
                ),
                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.carService.controls.needsTravel.valueChanges.pipe(
                    takeUntil(this.newTravelCard),
                    startWith(formValue.travelCard.travelDetails.carService.needsTravel),
                    map((value) => {
                        return {
                            value,
                            controls: <PersistentFormGroup[]>[
                                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.carService
                                    .controls.travel.controls.requestDetails,
                                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.carService
                                    .controls.travel.controls.preferences,
                            ],
                        };
                    })
                ),
                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.other.controls.needsTravel.valueChanges.pipe(
                    takeUntil(this.newTravelCard),
                    startWith(formValue.travelCard.travelDetails.other.needsTravel),
                    map((value) => {
                        return {
                            value,
                            controls: <PersistentFormGroup[]>[
                                this.requestFormGroup.controls.travelCard.controls.travelDetails.controls.other.controls
                                    .travel.controls.requestDetails,
                            ],
                        };
                    })
                )
            )
                .pipe(
                    takeUntil(this.newTravelCard),
                    filter((result) => !this.requestFormGroup.controls.travelCard.disabled),
                    tap((result) => {
                        result.controls.forEach((control) =>
                            result.value
                                ? control.enable({ breakPersistence: true, onlySelf: true, emitEvent: false })
                                : control.disable({ persistent: true, onlySelf: true, emitEvent: false })
                        );
                    })
                )
                .subscribe();

            result.isUpdate
                ? this.requestFormGroup.controls.travelCard.disable({ emitEvent: false })
                : this.requestFormGroup.controls.travelCard.enable({ emitEvent: false });

            this.travelViewModelService.travelFormGroup.set(this.requestFormGroup.controls.travelCard);
        }),
        map(([result, historyData]) => {
            const formGroupValue = this.requestFormGroup.getRawValue();
            return {
                historyData,
                countryOptions: result.countries.map(
                    (country) => <MdsOptionGeneric<CountryViewModel>>{ value: country, viewValue: country.viewValue }
                ),
                currencyOptions: result.currencies.map(
                    (currency) =>
                        <MdsOptionGeneric<CurrencyViewModel>>{
                            value: currency,
                            viewValue: currency.isocode,
                        }
                ),
                timeOptions: result.timeOptions,
                patientBookable:
                    formGroupValue.travelCard.travelDetails.flight.needsTravel ||
                    formGroupValue.travelCard.travelDetails.lodging.needsTravel ||
                    formGroupValue.travelCard.travelDetails.rentalCar.needsTravel,
                isUpdate: result.isUpdate,
                id: this.request.id,
            };
        }),
        tap((vm: TravelCardViewModel) => {
            this.travelViewModelService.travelCardViewModel.set(vm);
            this.loadingViewTriggerService.closeLoading({ force: true });
        }) // break persistence
    );

    constructor() {
        this.requestFormGroup = this.fb.group({
            travelCard: <TravelRequestCardFormGroup>null,
            VisitDetails: <AbstractControl>null,
        });
        this.requestFormGroup.controls.paymentCard = new PaymentRequestCardFormGroup();

        if (this.activeRoute.snapshot.params?.requestId) this.isNewRequest = false;
    }

    ngOnInit(): void {
        combineLatest({
            request: this.request$,
            patient: this.patient$,
            site: this.site$,
            study: this.study$,
            visitTypes: this.visitTypes$,
        })
            .pipe(
                tap(({ request, patient, site, study, visitTypes }) => {
                    if (request) {
                        this.request = request;
                        this.requestSignal.set(request);
                    }
                    if (patient) this.patient = patient;
                    if (site) {
                        this.site = site;
                        this.siteServices = site.services;
                    }
                    if (study) {
                        this.study = study;
                        this.studyTravelOptions = this.study.studyInfo.studyTravelOptions;
                    }
                    if (visitTypes) this.visitTypes = visitTypes;
                    if (study?.studyInfo?.expenseTypes)
                        this.expenseTypes = this.mapExpenseTypesToMdsOptionArray(study.studyInfo.expenseTypes);
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe();

        this.isCompletable = true;

        this.ObserveFileSizeLimit();
        this.ObserveVisitDetailsFormGroup();
    }

    showBookButton(patientBookable: boolean, requestStatusId: number): boolean {
        return (
            patientBookable &&
            this.elevatedAccess &&
            (requestStatusId == TravelRequestStatus.Submitted ||
                requestStatusId == TravelRequestStatus.BookingInProcess)
        );
    }

    private ObserveVisitDetailsFormGroup() {
        this.createRequestVisitDetailsSubject
            .pipe(
                tap((visitDetailsComponent) => {
                    if (visitDetailsComponent) {
                        visitDetailsComponent.formGroupChangeEvent
                            .pipe(
                                tap((event) => {
                                    this.requestFormGroup.setControl('VisitDetails', event);
                                }),
                                takeUntil(this.componentDestroyed$)
                            )
                            .subscribe();
                    }
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe();
    }
    public onEditVisitDetailsSave(data: MedpaceEditRequestVisitDetailsComponent.Data) {
        this.addEditRequestVisitDetails_Data();
        this.submitIncrementalSave();
    }
    ngOnDestroy() {
        this.historyDataService.clearRepositoryCache();
        this.componentDestroyed$.next(true);
        this.componentDestroyed$.complete();

        if (this.requestSignal()?.type === RequestTypes.TRAVEL) {
            this.travelViewModelService.clearState();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.site && changes.site.currentValue) {
            if (changes.siteServices && changes.siteServices.currentValue) {
                this.siteServices = changes.siteServices.currentValue;
                const services = this.siteServices;
                services.travel ? (this.travelAvailable = true) : (this.travelAvailable = false);
                services.reimbursement ? (this.reimbursementAvailable = true) : (this.reimbursementAvailable = false);
                services.stipend ? (this.stipendAvailable = true) : (this.stipendAvailable = false);
                this.stipendAvailable || this.reimbursementAvailable
                    ? (this.paymentAvailable = true)
                    : (this.paymentAvailable = false);
            }
        }
        if (!this.request) {
            this.steps = this.defaultsteps;
        }

        if (changes.patient && changes.patient.currentValue) {
            this.patient = changes.patient.currentValue;
        }

        if (changes.request && changes.request.currentValue) {
            if (changes.request.currentValue.status === undefined) {
                changes.request.currentValue.status = 0;
            }
        }

        this.maxSummaryFileSizesInMB = maxSizeOfFilesInMB;
        this.componentDisplayHandler();
    }

    ngDoCheck(): void {
        this.componentDisplayHandler();
        this.updateStatusName();
    }

    componentDisplayHandler(): void {
        if (this.request === null || this.request === undefined) {
            this.request = <GenericRequest>{
                travelRequests: <TravelAccommodations>{},
                status: <RequestStatus>{ statusId: 0, statusName: GenericRequestStatus[GenericRequestStatus.New] },
            };
            this.showPaymentCard = false;
            this.showTravelCards = false;
        }
        if (
            this.request.type === 'payment' ||
            this.request.type === 'reimbursement' ||
            this.request.type === 'stipend'
        ) {
            this.showPaymentCard = true;
            this.showTravelCards = false;
            this.steps[3] = this.visitDetailsStep;
            this.steps[4] = this.paymentStep;
        }
        if (this.request.type === 'travel') {
            this.showPaymentCard = false;

            this.showTravelCards = true;
            this.steps[3] = this.visitDetailsStep;
            this.steps[4] = this.travelStep;
        }
    }

    @HostListener('window:scroll', ['$event'])
    handleScroll(event: any) {
        this.updateStatusName();
    }

    submitIncrementalSave(specificCase?: boolean) {
        if (this.allowIncrementalSave(specificCase)) {
            this.validationMessage = '';
            this.incrementalSave.emit(this.request);
        }
    }

    allowIncrementalSave(specificCase?: boolean) {
        return this.elevatedAccess || (!this.elevatedAccess && this.request.status.statusId <= 2) || specificCase;
    }

    saveDraftClick(): void {
        if (this.request.type === 'payment') {
            this.requestFormGroup.recursiveUpdateValueAndValidity();
        }
        if (this.requestFormGroup.invalid) {
            // disabled state is being handled somehow by the code below
            this.requestFormGroup.markAllAsTouched();
            return;
        }
        if (this.isButtonSubmitActive()) {
            if (this.request.status.statusId === GenericRequestStatus.New)
                this.request.status.statusId = GenericRequestStatus.Draft;

            if (this.requestFormGroup.valid || this.requestFormGroup.disabled) {
                if (this.request.type === 'payment') {
                    this.request.reimbursement = this.paymentRequestCard().getReimbursement();
                    this.request.paymentType = this.request.reimbursement.type;
                }
                if (this.request.type === 'travel') {
                    this.request = this.applyTravelCardData(
                        this.request,
                        this.requestFormGroup.controls.travelCard.getRawValue()
                    );
                    this.loadingViewTriggerService.openLoading({ persistent: false }); //no persistent loading, the user will be redirected
                }
                this.isUpdate ? this.addEditRequestVisitDetails_Data() : this.addCreateRequestVisitDetails_Data();

                this.saveClickEvent.emit(this.request);
            } else {
                markFormGroupTouched(this.requestFormGroup);
            }
        }
    }
    submitRequest(): void {
        if (this.request.type === 'payment') {
            this.requestFormGroup.recursiveUpdateValueAndValidity();
        }
        if (this.requestFormGroup.invalid) {
            // disabled state is being handled somehow by the code below
            this.requestFormGroup.markAllAsTouched();
            return;
        }
        //need to check the expense attachments
        if (this.isButtonSubmitActive()) {
            if (this.requestFormGroup.valid || this.requestFormGroup.disabled) {
                const dialogRef = this.dialog.open(MedpaceAdditionalDetailsModalComponent, {
                    ...getModalConfig(ModalSize.Small),
                });
                dialogRef.afterClosed().subscribe((result) => {
                    if (result && result.save) {
                        if (this.request.status.statusId === GenericRequestStatus.Draft) {
                            this.request.status.statusId = GenericRequestStatus.Submitted;
                        }

                        this.request.additionalDetails = result.info;

                        if (this.request.type === 'payment') {
                            this.request.reimbursement = this.paymentRequestCard().getReimbursement();
                            this.request.paymentType = this.request.reimbursement.type;

                            if (this.request.status.statusId === PaymentRequestStatus.PendingSiteVerification)
                                this.request.status.statusId = GenericRequestStatus.Submitted;
                        }
                        if (this.request.type === 'travel') {
                            this.request = this.applyTravelCardData(
                                this.request,
                                this.requestFormGroup.controls.travelCard.getRawValue()
                            );
                            this.loadingViewTriggerService.openLoading({ persistent: false }); //no persistent loading, the user will be redirected
                            if (this.request.status.statusId === TravelRequestStatus.PendingSiteVerification)
                                this.request.status.statusId = GenericRequestStatus.Submitted;
                        }
                        this.isUpdate
                            ? this.addEditRequestVisitDetails_Data()
                            : this.addCreateRequestVisitDetails_Data();
                        this.submitClickEvent.emit(this.request);
                    }
                });
            } else {
                markFormGroupTouched(this.requestFormGroup);
            }
        }
    }

    private addEditRequestVisitDetails_Data() {
        const data = this.editRequestVisitDetailsSubject.getValue().data;
        if (data.requestType === RequestTypes.PAYMENT) {
            this.request.visitDetails.visitStartDate = data.paymentRequest_Data.visitDate;
            this.request.visitDetails.visitName = data.paymentRequest_Data.visitName;
            this.request.visitDetails.visitNameId = data.paymentRequest_Data.visitName.id;
        } else if (data.requestType === RequestTypes.TRAVEL) {
            this.request.visitDetails.travelerInstructions = data.travelRequest_Data.specialNavigationInstructions;
            this.request.visitDetails.visitName = data.travelRequest_Data.visitName;
            this.request.visitDetails.visitNameId = data.travelRequest_Data.visitName.id;

            this.request.visitDetails.visitStartDate = data.travelRequest_Data.visitStartDateTime;
            this.request.visitDetails.visitEndDate = data.travelRequest_Data.visitEndDateTime;
            this.request.visitDetails.visitStartTime = this.datePipe.transform(
                data.travelRequest_Data.visitStartDateTime?.toDate(),
                'HH:mm',
                'utc',
                'en-US'
            );
            this.request.visitDetails.visitEndTime = this.datePipe.transform(
                data.travelRequest_Data.visitEndDateTime?.toDate(),
                'HH:mm',
                'utc',
                'en-US'
            );
        }
    }
    private addCreateRequestVisitDetails_Data() {
        const data = this.createRequestVisitDetailsSubject.getValue().data;
        this.request.visitDetails = new VisitDetails();
        if (data.requestType === RequestTypes.PAYMENT) {
            this.request.visitDetails.visitStartDate = data.paymentRequest_Data.visitDate;
            this.request.visitDetails.visitName = data.paymentRequest_Data.visitName;
            this.request.visitDetails.visitNameId = data.paymentRequest_Data.visitName.id;
        } else if (data.requestType === RequestTypes.TRAVEL) {
            this.request.visitDetails.travelerInstructions = data.travelRequest_Data.specialNavigationInstructions;
            this.request.visitDetails.visitName = data.travelRequest_Data.visitName;
            this.request.visitDetails.visitNameId = data.travelRequest_Data.visitName.id;

            this.request.visitDetails.visitStartDate = data.travelRequest_Data.visitStartDateTime;
            this.request.visitDetails.visitEndDate = data.travelRequest_Data.visitEndDateTime;
            this.request.visitDetails.visitStartTime = this.datePipe.transform(
                data.travelRequest_Data.visitStartDateTime?.toDate(),
                'HH:mm',
                'utc',
                'en-US'
            );
            this.request.visitDetails.visitEndTime = this.datePipe.transform(
                data.travelRequest_Data.visitEndDateTime?.toDate(),
                'HH:mm',
                'utc',
                'en-US'
            );
        }
    }
    completeRequest(newStatus: number, requestType: RequestType, totalAuthorized?: number): void {
        if (
            requestType !== RequestType.Travel &&
            ((this.siteServices?.kycDocumentation == true && !this.patient.patientIdentification.KYCDocumentInfoId) ||
                (this.siteServices?.readyForPayment == true && !this.patient.readyForPayment))
        ) {
            this.dialog.open(MedpaceRequestErrorModalComponent, { disableClose: true });
        } else {
            this.request.status.statusId = newStatus;
            if (totalAuthorized) this.request.reimbursement.authorizedAmount = totalAuthorized;
            this.submitClickEvent.emit(this.request);
        }
    }

    changeStatus(): void {
        if (this.isChangeStatusButtonDisable()) return;

        const reqType: RequestType =
            this.request.type === 'travel'
                ? RequestType.Travel
                : this.request.paymentType === 'stipend'
                  ? RequestType.Stipend
                  : RequestType.Reimbursement;

        this.isCompletable = !(
            reqType !== RequestType.Travel &&
            this.siteServices?.readyForPayment == true &&
            !this.patient?.readyForPayment
        );

        if (reqType === RequestType.Travel) {
            this.dialog
                .open<
                    TravelRequestStatusChangeModalComponent,
                    TravelRequestStatusChangeModalComponentData,
                    TravelRequestStatusChangeModalComponentResult
                >(TravelRequestStatusChangeModalComponent, {
                    data: {
                        requestId: this.request.id,
                    },
                    ...getModalConfig(ModalSize.Small),
                })
                .afterClosed()
                .subscribe((result) => {
                    if (!result) return;

                    this.request.additionalDetails = result.additionalInfo;

                    if (result.newStatus === TravelRequestStatus[TravelRequestStatus.PendingInvoice]) {
                        return this.completeRequest(TravelRequestStatus.PendingInvoice, reqType);
                    }

                    const requestDTO: ChangeRequestStatusRequest = <ChangeRequestStatusRequest>{
                        requestId: this.request.id,
                        patientId: this.request.patientId,
                        status: this.request.status,
                        additionalDetails: result.additionalInfo,
                        newStatus: result.newStatus,
                        requestType: reqType,
                    };

                    this.statusChangeEvent.emit(requestDTO);
                });
            return;
        }

        this.dialog
            .open<
                PaymentRequestStatusChangeModalComponent,
                PaymentRequestStatusChangeModalComponentData,
                PaymentRequestStatusChangeModalComponentResult
            >(PaymentRequestStatusChangeModalComponent, {
                data: {
                    requestId: this.request.id,
                    requestType: reqType,
                },
                ...getModalConfig(ModalSize.Small),
            })
            .afterClosed()
            .subscribe((result) => {
                if (!result) return;

                this.request.additionalDetails = result.additionalInfo;

                if (result.newStatus === PaymentRequestStatus[PaymentRequestStatus.PendingERSubmission]) {
                    return this.completeRequest(PaymentRequestStatus.PendingERSubmission, reqType);
                }

                const requestDTO: ChangeRequestStatusRequest = <ChangeRequestStatusRequest>{
                    requestId: this.request.id,
                    patientId: this.request.patientId,
                    status: this.request.status,
                    additionalDetails: result.additionalInfo,
                    newStatus: result.newStatus,
                    requestType: reqType,
                    deducted: result.deducted.deducted,
                    deductedUsd: result.deducted.deductedUsd,
                    deductedCurrency: result.deducted.deductedCurrency?.isocode,
                };

                this.statusChangeEvent.emit(requestDTO);
            });
    }

    showSwitchModeButtonInVisitDetailsCard(): boolean {
        if (this.elevatedAccess) return true;

        return this.isAvailableToEdit();
    }

    isAvailableToEdit() {
        if (
            this.request.status.statusId === GenericRequestStatus.Draft ||
            this.request.status.statusId === GenericRequestStatus.Submitted
        )
            return true;
        else if (
            this.request.type == 'payment' &&
            this.request.status.statusId === PaymentRequestStatus.PendingSiteVerification
        )
            return true;
        else if (
            this.request.type == 'travel' &&
            this.request.status.statusId === TravelRequestStatus.PendingSiteVerification
        ) {
            return true;
        }

        return false;
    }

    viewHistory(): void {
        this.requestStatusLog = [];
        let request: Observable<GetRequestEventLogResponse[]>;
        if (this.request.type === 'travel') {
            this.loadingViewTriggerService.openLoading();
            request = this.generatedRequestService
                .requestControllerv2GetTravelEventLogs(this.request.id)
                .pipe(map((response) => response.logs));
        } else if (
            this.request.type === 'payment' ||
            this.request.type === 'stipend' ||
            this.request.type === 'out-of-pocket'
        ) {
            if (this.request.paymentType === 'stipend') {
                request = this.generatedRequestService
                    .requestControllerv2GetStipendEventLogs(this.request.id)
                    .pipe(map((response) => response.logs));
            } else if (this.request.paymentType === 'out-of-pocket') {
                request = this.generatedRequestService
                    .requestControllerv2GetReimbursementEventLogs(this.request.id)
                    .pipe(map((response) => response.logs));
            }
        }

        request.subscribe((result) => {
            this.loadingViewTriggerService.closeLoading();
            this.dialog.open(MedpaceViewHistoryModalComponent, {
                data: result,
                ...getModalConfig(ModalSize.Large),
            });
        });
    }

    deleteRequest(): void {
        const dialogRef = this.dialog.open(MedpaceDeleteRequestModalComponent, {
            data: this.request,
            ...getModalConfig(ModalSize.Small),
        });
        dialogRef.afterClosed().subscribe((result) => {
            let response;
            if (result) {
                if (this.request.type === 'travel') {
                    response = this.requestServices.deleteTravelRequest(this.request.id);
                } else if (this.request.type === 'stipend' || this.request.paymentType === 'stipend') {
                    response = this.requestServices.deleteStipendRequest(this.request.id);
                } else if (this.request.type === 'reimbursement' || this.request.paymentType === 'out-of-pocket') {
                    response = this.requestServices.deleteReimbursementRequest(this.request.id);
                }

                response.subscribe((result) => {
                    this.router
                        .navigate([
                            `studies/${this.study.id}/sites/${this.site.id}/patients/${this.patient.id}/overview`,
                        ])
                        .then((navigated: boolean) => {
                            if (navigated) {
                                this.snackbarService.openInfoSnackbar('Deleted Request');
                            }
                        });
                });
            }
        });
    }

    fieldChangeEvent(event: InputChange): void {
        if (event.target === 'requestType' && event.value === 'travel') {
            this.request.type = 'travel';
            this.componentDisplayHandler();
            this.steps[4] = this.paymentStep;
            this.requestTypeSubject.next(RequestTypes.TRAVEL);
        }

        if (event.target === 'requestType' && event.value === 'payment') {
            this.request.type = 'payment';
            this.componentDisplayHandler();
            this.steps[4] = this.travelStep;
            this.requestTypeSubject.next(RequestTypes.PAYMENT);
        }
        if (event.target === 'flightTransDate') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'flightTransAmount') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'flightTransCurrency') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'flightDoc') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'trainTransDate') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'trainCost') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'trainCurrency') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'trainDoc') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'lodgingCheckInDate') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'lodgingCheckOutDate') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'lodgingDateRequired') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'lodgingTransDate') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'lodgingCost') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'lodgingCurrency') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'lodgingDoc') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'groundTransDate') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'groundCost') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'groundCurrency') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'carService') {
            const transferData = event.value.transferDatas as {
                pickupDate: Moment;
                pickupTime: number;
                pickupAddress: string;
                dropoffAddress: string;
            }[];
            this.request.travelRequests.transferDatas = transferData.map(
                (data) =>
                    <TransferData>{
                        dropoffAddress: data.dropoffAddress ?? '',
                        pickupAddress: data.pickupAddress ?? '',
                        pickupDateTime:
                            data.pickupDate && data.pickupTime !== null && data.pickupTime !== undefined
                                ? data.pickupDate.add(data.pickupTime, 'minutes')
                                : null,
                    }
            );
        }
        if (event.target === 'otherTransDate') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'otherCost') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'otherCurrency') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'otherDoc') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'otherRequests') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'needsRentalCar') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'rentalCarRequests') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'rentalCarDate') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'rentalCarCost') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'rentalCarCurrency') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'rentalCarDoc') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'rentalCarPickUpTime') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'rentalCarDropOffTime') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'rentalCarPickUpDate') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'rentalCarDropOffDate') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'rentalCarCountry') {
            this.request.travelRequests[event.target] = event.value;
        }
        if (event.target === 'rentalCarLocation') {
            this.request.travelRequests[event.target] = event.value;
        }

        if (event.target.indexOf('needsAir') !== -1) {
            if (event.target === 'needsAirTravel') {
                if (!this.request.travelRequests) {
                    this.request.travelRequests = <TravelAccommodations>{};
                }
                this.request.travelRequests.needsAirTravel = event.value;
            }
            if (event.target === 'needsAir' && this.request.travelRequests.needsAirTravel) {
                this.request.travelRequests.flightRequests = event.value;
            }
        }
        if (event.target.indexOf('needsTrain') !== -1) {
            if (event.target === 'needsTrainTravel') {
                if (!this.request.travelRequests) {
                    this.request.travelRequests = <TravelAccommodations>{};
                }
                this.request.travelRequests.needsTrainTravel = event.value;

                if (event.value === false) {
                    this.request.travelRequests.trainTravelDetailsForPatient = null;
                    if (this.request.travelRequestCaregivers) {
                        for (const caregiver of this.request.travelRequestCaregivers) {
                            caregiver.trainTravelDetails = null;
                        }
                    }
                }
            }

            if (event.target === 'needsTrain' && this.request.travelRequests.needsTrainTravel) {
                this.request.travelRequests.trainRequests = event.value;
            }
        }
        if (event.target.indexOf('needsLodging') !== -1) {
            if (event.target === 'needsLodging') {
                if (!this.request.travelRequests) {
                    this.request.travelRequests = <TravelAccommodations>{};
                }
                this.request.travelRequests.needsLodging = event.value;
            }
            if (event.target === 'needsLodgingAccess' && this.request.travelRequests.needsLodging) {
                this.request.travelRequests.lodgingRequests = event.value;
            }
        }
        if (event.target.indexOf('needsGround') !== -1) {
            if (event.target === 'needsGroundTransportation') {
                if (!this.request.travelRequests) {
                    this.request.travelRequests = <TravelAccommodations>{};
                }
                this.request.travelRequests.needsCar = event.value;
            }
            if (event.target === 'needsGround' && this.request.travelRequests.needsCar) {
                this.request.travelRequests.carServiceRequests = event.value;
            }
        }

        if (event.target.indexOf('rentalCarRequest') !== -1) {
            if (event.target === 'needsRentalCar') {
                if (!this.request.travelRequests) {
                    this.request.travelRequests = <TravelAccommodations>{};
                }
                this.request.travelRequests.needsRentalCar = event.value;
            }
            if (event.target === 'rentalCarRequest' && this.request.travelRequests.needsRentalCar) {
                this.request.travelRequests.rentalCarRequests = event.value;
            }
        }

        if (event.target.indexOf('needsOther') !== -1) {
            if (event.target === 'needsOtherAccomodations') {
                if (!this.request.travelRequests) {
                    this.request.travelRequests = <TravelAccommodations>{};
                }
                this.request.travelRequests.needsOther = event.value;
            }
            if (event.target === 'needsOther' && this.request.travelRequests.needsCar) {
                this.request.travelRequests.otherRequests = event.value;
            }
        }
        if (event.target === 'hasCaregiverTravelling') {
            this.request.caregiver = event.value;
        }
        if (event.target === 'minorPatient') {
            this.request.isMinor = event.value;
        }
        if (event.target === 'travelCaregiver') {
            if (!this.request.travelRequestCaregivers) {
                this.request.travelRequestCaregivers = [];
            }

            const eventValue = event.value as TravelRequestCaregiver;

            if (eventValue.isChecked) {
                this.request.travelRequestCaregivers.push(eventValue);
            } else {
                const foundCaregiverIndex = this.request.travelRequestCaregivers.findIndex(
                    (t) => t.caregiverId === eventValue.caregiverId
                );
                if (foundCaregiverIndex > -1) {
                    if (this.request.travelRequestCaregivers[foundCaregiverIndex].travelRequestId) {
                        this.requestServices
                            .deleteTravelRequestCaregiver(
                                this.request.travelRequestCaregivers[foundCaregiverIndex].travelRequestId,
                                this.request.travelRequestCaregivers[foundCaregiverIndex].caregiverId
                            )
                            .subscribe(() => this.request.travelRequestCaregivers.splice(foundCaregiverIndex, 1));
                    } else {
                        this.request.travelRequestCaregivers.splice(foundCaregiverIndex, 1);
                    }
                }
            }
            this.eventService.emit(new EmittedEvent('travelCaregiversChanged', null));
        }

        if (event.target == 'trainTravelCaregivers') {
            const caregiversTrainTravels = event.value as TrainTravelDetailsFormModel[];

            for (const trainTravelPlan of caregiversTrainTravels) {
                const caregiverInRequest = this.request.travelRequestCaregivers.find(
                    (x) => x.caregiverId == trainTravelPlan.caregiverId
                );
                if (!caregiverInRequest)
                    throw Error('Assertion failed: caregiver list should be in sync in both component');

                caregiverInRequest.trainTravelDetails = mapTrainTravelFormModelToTrainTravelDetails(trainTravelPlan);

                if (
                    caregiverInRequest.trainTravelDetails.isSameAsPatient ||
                    caregiverInRequest.trainTravelDetails.trainTravelType == TrainTravelType.NoTravel
                ) {
                    // don't send data from the hidden/irrelevant parts of the form to the backend, as it might trigger validation errors
                    caregiverInRequest.trainTravelDetails.departureDate = null;
                    caregiverInRequest.trainTravelDetails.returnDate = null;
                    caregiverInRequest.trainTravelDetails.specialRequests = null;
                    caregiverInRequest.trainTravelDetails.trainStationOrigin = null;
                    caregiverInRequest.trainTravelDetails.trainStationDestination = null;
                }
            }
        }

        if (event.target == 'trainTravelPatient') {
            const patientTrainTravelPlan = event.value as TrainTravelDetailsFormModel;

            if (patientTrainTravelPlan.trainFormInLegacyMode) {
                this.request.travelRequests.trainRequests = patientTrainTravelPlan.trainSpecialRequests;
            } else {
                this.request.travelRequests.trainTravelDetailsForPatient =
                    mapTrainTravelFormModelToTrainTravelDetails(patientTrainTravelPlan);
            }
        }

        this.requestSignal.set({ ...this.request });
        this.requestChangeEvent.emit(this.request);
        this.updateStatusName();
    }

    updateStatusName() {
        if (this.request.status.statusId !== GenericRequestStatus.New) {
            this.actualStatus = this.request?.status.statusName;
        } else this.actualStatus = '';
    }

    handlePaymentChange(event: any) {
        this.request.paymentType = event.target;
        this.request.reimbursement = event.value;
    }
    public onPaymentRequestExpenseDetailsSave(reimbursement: Reimbursement) {
        this.request.reimbursement = reimbursement;
        this.request.paymentType = this.request.reimbursement.type;
        this.submitIncrementalSave();
    }

    patientNavigation(event: any) {
        this.patientNavigationRequestEvent.emit(event);
    }

    checkStatusOption(chosenOption: any) {
        switch (chosenOption) {
            case 'Change Status':
                this.changeStatus();
                break;
            case 'View History':
                this.viewHistory();
                break;
            case 'Delete Request':
                this.deleteRequest();
                break;
            default:
                console.error('Status not recognized!');
                break;
        }
    }

    private ObserveFileSizeLimit() {
        this.attachmentSizeService.allUploadedFilesSizeExceeded$
            .pipe(
                takeUntil(this.componentDestroyed$),
                tap((filesSizeExceeded) => this.isButtonSubmitActive.set(!filesSizeExceeded))
            )
            .subscribe();
    }
    public isUpdateAndElevatedAccess(): boolean {
        return this.isUpdate && this.elevatedAccess;
    }
    public isRequestStatus(...statuses: number[]): boolean {
        return !!this.request && statuses.includes(this.request.status.statusId);
    }
    public travelCardSave(data: FormGroupValueOf<TravelRequestCardFormGroup>) {
        this.request = this.applyTravelCardData(this.request, data);
        this.loadingViewTriggerService.openLoading({ persistent: true }); // persistent loading to prevent loading.interceptor from disabling it when the GET request arrives
        this.submitIncrementalSave();
    }
    travelRequestCard = viewChild<TravelCardComponent>(TravelCardComponent);
    private applyTravelCardData(request: GenericRequest, data: FormGroupValueOf<TravelRequestCardFormGroup>) {
        const patientFlightDetails = data.travelDetails.flight.travel.requestDetails.patientFlightDetails;
        const patientTrainDetails = data.travelDetails.train.travel.requestDetails.patientTrainTripDetails;
        const patientLodgingDetails = data.travelDetails.lodging.travel.requestDetails;
        const patientCarRentalDetails = data.travelDetails.rentalCar.travel.requestDetails;
        const patientTransferDetails = data.travelDetails.carService.travel.requestDetails;

        request.receipts = this.travelRequestCard()
            .receipts()
            .map((receiptComponent) => receiptComponent.push({ travelRequestId: request.id }));

        forkJoin([
            this.countryStateService.getCountries().pipe(take(1)),
            this.patientStateService.getSummaryPatientData().pipe(take(1)),
        ]).subscribe(([countries, patient]: [CountryViewModel[], Patient]) => {
            request = {
                ...request,

                travelRequests: {
                    ...request.travelRequests,
                    needsAirTravel: data.travelDetails.flight.needsTravel,

                    needsTrainTravel: data.travelDetails.train.needsTravel,

                    needsLodging: data.travelDetails.lodging.needsTravel,

                    needsRentalCar: data.travelDetails.rentalCar.needsTravel,

                    needsCar: data.travelDetails.carService.needsTravel,

                    needsOther: data.travelDetails.other.needsTravel,

                    rentalCarUseFlightDate:
                        data.travelDetails.rentalCar.travel.requestDetails.useFlightDate &&
                        data.travelDetails.flight.needsTravel,
                },
                travelRequestCaregivers: [
                    data.accompanyingCaregivers.primaryCaregiver,
                    ...data.accompanyingCaregivers.otherCaregivers,
                ]
                    .filter((caregiver) => caregiver.checked)
                    .map((c) => {
                        const flightDetailsForCaregiver =
                            data.travelDetails.flight.travel.requestDetails.caregivers.find(
                                (caregiver) => caregiver.caregiver.id === c.value.caregiver.id
                            );

                        // null value = caregiver doesn't need a flight booked
                        let flightDetails: FormGroupValueOf<SingleFlightFormGroup> = null;
                        if (flightDetailsForCaregiver?.details?.bookIdenticalTrip) {
                            flightDetails = {
                                ...patientFlightDetails,
                                id: null,
                            };
                        } else if (flightDetailsForCaregiver?.details?.flightDetails) {
                            flightDetails = flightDetailsForCaregiver.details.flightDetails;
                        }

                        const flightRequestDetails = !(
                            flightDetailsForCaregiver.details.bookIdenticalTrip ||
                            flightDetailsForCaregiver.details.flightDetails.travelType !== TravelType.NO_FLIGHT
                        )
                            ? null
                            : {
                                  airportDestination: flightDetails.airportDestination,
                                  airportOrigin: flightDetails.airportOrigin,
                                  departureDate: flightDetails.departureDate,
                                  returnDate: flightDetails.returnDate,
                                  specialRequests: flightDetails.specialRequests,
                                  travelType: flightDetails.travelType,
                                  id: flightDetails.id,
                              };
                        const trainGeneralDetails = data.travelDetails.train.travel.requestDetails.caregivers.find(
                            (caregiver) => caregiver.caregiver.id === c.value.caregiver.id
                        );
                        const trainDetails = !trainGeneralDetails?.details.bookIdenticalTrip
                            ? trainGeneralDetails.details.trainDetails
                            : patientTrainDetails;
                        const trainTravelDetails = !(
                            trainGeneralDetails.details.bookIdenticalTrip ||
                            trainGeneralDetails.details.trainDetails.travelType !== TrainTravelType.NoTravel
                        )
                            ? null
                            : {
                                  trainStationOrigin: trainDetails.stationOrigin,
                                  trainStationDestination: trainDetails.stationDestination,
                                  departureDate: trainDetails.departureDate,
                                  returnDate: trainDetails.returnDate,
                                  isSameAsPatient: !!trainGeneralDetails?.details.bookIdenticalTrip,
                                  specialRequests: trainDetails.specialRequests,
                                  trainTravelType: trainDetails.travelType,
                                  trainFormInLegacyMode: false,
                              };
                        const travelRequestCaregiver: TravelRequestCaregiver = {
                            caregiverId: c.value.caregiver.id,
                            caregiverName: getCaregiverFullName(c.value.caregiver),
                            isChecked: c.checked,
                            isPrimary: c.value.caregiver.isPrimary,
                            flightRequestTheSameAsPatient: null,
                            flightRequestDetails: null,
                            flightRequestDetailsId: null,
                            trainTravelDetails: null,
                            travelRequestId: request.id,
                        };
                        if (data.travelDetails.flight.needsTravel) {
                            travelRequestCaregiver.flightRequestTheSameAsPatient =
                                !!flightDetailsForCaregiver?.details.bookIdenticalTrip;
                            travelRequestCaregiver.flightRequestDetails = flightRequestDetails;
                            travelRequestCaregiver.flightRequestDetailsId = flightRequestDetails
                                ? flightRequestDetails.id
                                : null;
                        }
                        if (data.travelDetails.train.needsTravel) {
                            travelRequestCaregiver.trainTravelDetails = trainTravelDetails;
                        }
                        return travelRequestCaregiver;
                    }),
            };
            if (request.travelRequests.needsAirTravel) {
                request.travelRequests.flightRequestDetails = {
                    airportOrigin: patientFlightDetails.airportOrigin,
                    airportDestination: patientFlightDetails.airportDestination,
                    departureDate: patientFlightDetails.departureDate,
                    returnDate: patientFlightDetails.returnDate,
                    specialRequests: patientFlightDetails.specialRequests,
                    travelType: patientFlightDetails.travelType,
                    id: patientFlightDetails.id,
                };
                request.travelRequests.flightRequestDetailsId = patientFlightDetails.id;
                request.travelRequests.flightRequests = patientFlightDetails.specialRequests;
            }
            if (request.travelRequests.needsTrainTravel) {
                request.travelRequests.trainTravelDetailsForPatient = {
                    trainStationOrigin: patientTrainDetails.stationOrigin,
                    trainStationDestination: patientTrainDetails.stationDestination,
                    departureDate: patientTrainDetails.departureDate,
                    returnDate: patientTrainDetails.returnDate,
                    isSameAsPatient: true,
                    specialRequests: patientTrainDetails.specialRequests,
                    trainTravelType: patientTrainDetails.travelType,
                    trainFormInLegacyMode: false,
                };
                request.travelRequests.trainRequests = patientTrainDetails.specialRequests;
            }

            if (request.travelRequests.needsLodging) {
                request.travelRequests.lodgingCheckInDate = patientLodgingDetails.checkInDate;
                request.travelRequests.lodgingCheckOutDate = patientLodgingDetails.checkOutDate;
                request.travelRequests.lodgingRequests = patientLodgingDetails.specialRequests;
                request.travelRequests.lodgingDateRequired = false; // will not be used anymore
            }
            if (request.travelRequests.needsRentalCar) {
                if (request.travelRequests.rentalCarUseFlightDate) {
                    // set unused fields to null when using flight details
                    request.travelRequests.rentalCarLocation = null;
                    request.travelRequests.rentalCarCountry = null;
                    request.travelRequests.rentalCarPickUpDate = null;
                    request.travelRequests.rentalCarPickUpTime = null;
                    request.travelRequests.rentalCarDropOffDate = null;
                    request.travelRequests.rentalCarDropOffTime = null;
                } else {
                    request.travelRequests.rentalCarLocation = patientCarRentalDetails.pickupAddress;
                    request.travelRequests.rentalCarCountry =
                        countries.find((country) => country.viewValue === patientCarRentalDetails.pickupCountry)
                            ?.countryCode ?? '';
                    request.travelRequests.rentalCarPickUpDate = patientCarRentalDetails.pickupDate;
                    request.travelRequests.rentalCarPickUpTime =
                        this.timeOptionsStateService.convertMinutesSinceMidnightToTimeString(
                            patientCarRentalDetails.pickupTime,
                            false //pass false instead of 'this.timeOptionsStateService.is24HourFormat', the data from backend will be a 12-hour format string like '5:15 am'
                        );
                    request.travelRequests.rentalCarDropOffDate = patientCarRentalDetails.dropOffDate;
                    request.travelRequests.rentalCarDropOffTime =
                        this.timeOptionsStateService.convertMinutesSinceMidnightToTimeString(
                            patientCarRentalDetails.dropOffTime,
                            false //this.timeOptionsStateService.is24HourFormat
                        );
                }

                request.travelRequests.rentalCarRequests = patientCarRentalDetails.specialRequests;
            }

            if (request.travelRequests.needsCar) {
                request.travelRequests.carServiceRequests = patientTransferDetails.specialRequests;
                request.travelRequests.transferDatas = patientTransferDetails.transfers.map(
                    (transfer) =>
                        <TransferData>{
                            ...transfer,
                            pickupDateTime: transfer.pickupDate?.startOf('day').add(transfer.pickupTime, 'minutes'),
                        }
                );
            }

            if (request.travelRequests.needsOther) {
                request.travelRequests.otherRequests = data.travelDetails.other.travel.requestDetails.specialRequests;
            }
        });

        return request;
    }
    loadGetThere(getThereUrlParams: GetThereInputParameters) {
        const getThereUrlParamsDto = mapToDTO(getThereUrlParams);
        const getThereUrl$ = this.getThereService.getUrl(getThereUrlParamsDto);

        getThereUrl$
            .pipe(
                switchMap((response) => {
                    if (response.warnings && response.warnings.length > 0) {
                        let warningMessage = 'Please note the following:\n\n<ul>';
                        const bulletPoints = response.warnings.map((warning) => `<li>${warning}</li>`).join('\n');
                        warningMessage += bulletPoints;
                        warningMessage += '</ul>';

                        const dialogConfig = new MatDialogConfig();
                        dialogConfig.disableClose = false;
                        dialogConfig.autoFocus = true;
                        dialogConfig.maxWidth = ModalSize.Small;
                        dialogConfig.minWidth = ModalSize.Small;
                        dialogConfig.data = {
                            title: 'Warning: some values will be truncated',
                            bodyText: warningMessage,
                            showCancelButton: true,
                        };

                        const dialog = this.dialog.open(MessageDialogComponent, dialogConfig);
                        return combineLatest<[CreateGetThereUrlViewModel, ModalResult, boolean]>([
                            of(response),
                            dialog.afterClosed(),
                            of(true),
                        ]);
                    } else {
                        return combineLatest<[CreateGetThereUrlViewModel, undefined, boolean]>([
                            of(response),
                            of(undefined),
                            of(false),
                        ]);
                    }
                })
            )
            .pipe(
                switchMap(([response, modalResult, didModalOccur]) => {
                    if ((didModalOccur && modalResult === ModalResult.Okay) || !didModalOccur) {
                        window.open(response.getThereUrl.url, '_blank', 'noopener');
                    }
                    return of(response);
                })
            )
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
                error: (msg) => {
                    console.log('Error loading GetThere. Error message: ', msg);

                    this.dialog.open<DisplayErrorDialogComponent, DisplayErrorDialogData>(DisplayErrorDialogComponent, {
                        ...getModalConfig(ModalSize.Small),
                        data: {
                            description:
                                'There was an error loading GetThere.\nPlease contact the system administrator for assistance. \n\nError Message:\n' +
                                msg,
                        },
                    });
                    throw msg;
                },
            });
    }

    /**
     *
     * @param studyCode
     * @param siteNumber
     * @param userId - user's id in the PCS database (the database-generated one). Do NOT use the Patient Number (e.g. 001-002) here, as it's considered a personal information and we must not use it.
     * @param userType - 'c' for caregiver, 'p' for patient - used to prevent situation where id for patient and caregiver might be identical
     * @returns
     */
    private generateUserId(studyCode: string, userId: number, userType: 'c' | 'p'): string {
        let id = `${studyCode}-${userId}-${userType}`.toLowerCase();
        // Realistic example: "tst-0109-693045-p" (17 characters)

        // GetThere's limit for user_id is 64 characters (64 is fine, 65 isn't).
        // For longer id we generate sha256 hash (64 characters)
        if (id.length > 64) {
            const hashedId = sha256().update(id).digest('hex');
            if (hashedId.length !== 64) {
                //should never happen, sha256 generates a string of length 64
                throw Error(`Hashed ID for GetThere should have length 64, got ${hashedId.length}`);
            }

            const warningMessage = `Traveler's user id exceeds GetThere's limit of 64 character. Generated hashed id '${hashedId}' from '${id}'`;
            console.warn(warningMessage);

            id = hashedId;
        }

        return id;
    }
    bookViaGetThereForCaregiver(caregiver: Caregiver) {
        const travelerId = this.generateUserId(this.patient.studyCode, caregiver.id, 'c');
        const getThereTravelerId = !caregiver
            ? null
            : <GetThereTravelerId>{
                  travelerId: travelerId,
                  firstName: caregiver.firstName,
                  middleName: caregiver.middleName,
                  lastName: caregiver.lastName,
                  emailAddress: caregiver.email,
                  country: caregiver.country,
                  address1: caregiver.address1,
                  address2: caregiver.address2,
                  city: caregiver.city,
                  state: caregiver.state,
                  zipcode: caregiver.zipcode,
                  phoneNumber: caregiver.phone,
                  gender: caregiver.gender,
                  birthDate: caregiver.birthDate,
                  studyCode: this.patient.studyCode,
                  siteNumber: this.patient.siteNumber,
                  patientId: caregiver.patientId.toString(),
              };
        const value = this.requestFormGroup.controls.travelCard.getRawValue();
        const caregiverDetails = value.travelDetails.flight.travel.requestDetails.caregivers.find(
            (c) => c.caregiver.id === caregiver.id
        );
        this.loadGetThere(
            this.getThereService.createGetThereURLParams(
                getThereTravelerId,
                caregiverDetails.details.bookIdenticalTrip ||
                    caregiverDetails.details.flightDetails.travelType !== TravelType.NO_FLIGHT,
                value.travelDetails.flight.travel.requestDetails.caregivers.find((c) => c.caregiver.id === caregiver.id)
                    .details.flightDetails,
                value.travelDetails.lodging.needsTravel, // the rest is taken from patient
                value.travelDetails.lodging.travel.requestDetails,
                value.travelDetails.rentalCar.needsTravel,
                value.travelDetails.rentalCar.travel.requestDetails,
                this.request.visitDetails
            )
        );
    }
    bookViaGetThereForPatient() {
        const travelerId = this.generateUserId(this.patient.studyCode, this.patient.id, 'p');
        const getThereTravelerId =
            !this.patient || !this.patient.patientIdentification
                ? null
                : <GetThereTravelerId>{
                      travelerId: travelerId,
                      firstName: this.patient.patientIdentification.firstName,
                      middleName: this.patient.patientIdentification.middleName,
                      lastName: this.patient.patientIdentification.lastName,
                      emailAddress: this.patient.patientIdentification.emailAddress,
                      country: this.patient.patientIdentification.country,
                      address1: this.patient.patientIdentification.address1,
                      address2: this.patient.patientIdentification.address2,
                      city: this.patient.patientIdentification.city,
                      state: this.patient.patientIdentification.state,
                      zipcode: this.patient.patientIdentification.zipcode,
                      phoneNumber: this.patient.patientIdentification.phoneNumber,
                      gender: this.patient.patientIdentification.gender,
                      birthDate: this.patient.patientIdentification.birthDate,
                      studyCode: this.patient.studyCode,
                      siteNumber: this.patient.siteNumber,
                      patientId: this.patient.patientIdentification.patientId,
                  };
        const value = this.requestFormGroup.controls.travelCard.getRawValue();
        this.loadGetThere(
            this.getThereService.createGetThereURLParams(
                getThereTravelerId,
                value.travelDetails.flight.needsTravel,
                value.travelDetails.flight.travel.requestDetails.patientFlightDetails,
                value.travelDetails.lodging.needsTravel,
                value.travelDetails.lodging.travel.requestDetails,
                value.travelDetails.rentalCar.needsTravel,
                value.travelDetails.rentalCar.travel.requestDetails,
                this.request.visitDetails
            )
        );
    }

    mapExpenseTypesToMdsOptionArray(expenseTypes: StudyExpense[]): MdsOption[] {
        return expenseTypes.map((expense: StudyExpense): MdsOption => ({
            value: expense.expenseId,
            viewValue: expense.expenseDefaultValue,
        }));
    }

    showEditButton(requestStatus: number): boolean {
        if (requestStatus === GenericRequestStatus.Draft || requestStatus === GenericRequestStatus.ERSubmitted) {
            return false;
        } else return this.isUpdateAndElevatedAccess();
    }

    isSaveDraftButtonVisible = computed(() => {
        let request = this.requestSignal();

        if (!request) return false;
        else if (!request.type) return true; // new request => status = New

        switch (request.type) {
            case RequestTypes.TRAVEL:
                return this.request.status.statusId === TravelRequestStatus.New;
            case RequestTypes.PAYMENT:
                return this.request.status.statusId === PaymentRequestStatus.New;
            default:
                return false;
        }
    });

    isSubmitButtonVisible = computed(() => {
        let request = this.requestSignal();

        if (!request) return false;
        else if (!request.type) return true; // new request => status = New

        switch (request.type) {
            case RequestTypes.TRAVEL:
                return (
                    this.request.status.statusId === TravelRequestStatus.Draft ||
                    this.request.status.statusId === TravelRequestStatus.New ||
                    this.request.status.statusId === TravelRequestStatus.PendingSiteVerification
                );
            case RequestTypes.PAYMENT:
                return (
                    this.request.status.statusId === PaymentRequestStatus.Draft ||
                    this.request.status.statusId === PaymentRequestStatus.New ||
                    this.request.status.statusId === PaymentRequestStatus.PendingSiteVerification
                );
            default:
                return false;
        }
    });

    isViewHistoryButtonVisible = computed(() => {
        let request = this.requestSignal();

        if (!request || !request.type) return false;
        switch (request.type) {
            case RequestTypes.TRAVEL:
                return (
                    this.request.status.statusId !== TravelRequestStatus.New &&
                    this.request.status.statusId !== TravelRequestStatus.Draft
                );
            case RequestTypes.PAYMENT:
                return (
                    this.request.status.statusId !== PaymentRequestStatus.Draft &&
                    this.request.status.statusId !== PaymentRequestStatus.New
                );
            default:
                return false;
        }
    });

    isDeleteButtonVisible = computed(() => {
        const request = this.requestSignal();

        if (!request || !request.type || !this.elevatedAccess) return false;
        switch (request.type) {
            case RequestTypes.TRAVEL:
                return (
                    (request.status.statusId !== TravelRequestStatus.New &&
                        request.status.statusId !== TravelRequestStatus.PendingInvoice &&
                        request.status.statusId !== TravelRequestStatus.PendingERSubmission &&
                        request.status.statusId !== TravelRequestStatus.ERSubmitted &&
                        request.status.statusId !== TravelRequestStatus.Cancelled) ||
                    (request.status.statusId === TravelRequestStatus.Cancelled && request.receipts?.length === 0)
                );
            case RequestTypes.PAYMENT:
                return (
                    request.status.statusId !== PaymentRequestStatus.New &&
                    request.status.statusId !== PaymentRequestStatus.PendingERSubmission &&
                    request.status.statusId !== PaymentRequestStatus.ERSubmitted &&
                    request.status.statusId !== PaymentRequestStatus.Voided &&
                    request.status.statusId !== PaymentRequestStatus.PendingPSApproval &&
                    request.status.statusId !== PaymentRequestStatus.PQProcessing
                );
            default:
                return false;
        }
    });

    isChangeStatusButtonVisible = computed(() => {
        let request = this.requestSignal();

        if (!request || !request.type || !this.elevatedAccess) return false;

        switch (request.type) {
            case RequestTypes.TRAVEL:
                return (
                    request.status.statusId !== TravelRequestStatus.New &&
                    request.status.statusId !== TravelRequestStatus.Draft &&
                    request.status.statusId !== TravelRequestStatus.PendingSiteVerification
                );
            case RequestTypes.PAYMENT:
                return (
                    request.status.statusId !== PaymentRequestStatus.New &&
                    request.status.statusId !== PaymentRequestStatus.Draft &&
                    request.status.statusId !== PaymentRequestStatus.PendingSiteVerification
                );
            default:
                return false;
        }
    });

    isChangeStatusButtonDisable = computed(() => {
        let request = this.requestSignal();

        if (!request || !request.type || !this.elevatedAccess) return true;

        switch (request.type) {
            case RequestTypes.TRAVEL:
                return (
                    request.status.statusId === TravelRequestStatus.Denied ||
                    request.status.statusId === TravelRequestStatus.ERSubmitted
                );
            case RequestTypes.PAYMENT:
                return (
                    request.status.statusId === PaymentRequestStatus.Denied ||
                    request.status.statusId === PaymentRequestStatus.Voided ||
                    request.status.statusId === PaymentRequestStatus.PQProcessing
                );
            default:
                return false;
        }
    });

    changeStatusButtonTooltip = computed(() => {
        if (!this.isChangeStatusButtonDisable()) return '';

        let request = this.requestSignal();
        if (request?.type === RequestTypes.PAYMENT && request.status.statusId === PaymentRequestStatus.PQProcessing) {
            return 'Status cannot be changed while the request is being processed by PayQuicker.';
        }

        return 'Final request status cannot be changed.';
    });

    isUpdateButtonVisible = computed(() => {
        let request = this.requestSignal();

        if (!request || !request.type || this.elevatedAccess) return false;

        switch (request.type) {
            case RequestTypes.TRAVEL:
                return (
                    this.request.status.statusId === TravelRequestStatus.Submitted &&
                    this.patient?.statusId !== AllPatientStatusesEnum.Removed
                );
            case RequestTypes.PAYMENT:
                return (
                    this.request.status.statusId === PaymentRequestStatus.Submitted &&
                    this.patient?.statusId !== AllPatientStatusesEnum.Removed
                );
            default:
                return false;
        }
    });

    isPossibleToEditRequest = computed(() => {
        const request = this.requestSignal();

        if (!request?.type || !request.status) return false;
        if (request.type === RequestTypes.PAYMENT) {
            return (
                request.status.statusId !== PaymentRequestStatus.PendingPSApproval &&
                request.status.statusId !== PaymentRequestStatus.Denied &&
                request.status.statusId !== PaymentRequestStatus.PendingERSubmission &&
                request.status.statusId !== PaymentRequestStatus.ERSubmitted &&
                request.status.statusId !== PaymentRequestStatus.Voided &&
                request.status.statusId !== PaymentRequestStatus.PQProcessing
            );
        }

        return true;
    });

    reimbursementRequestService = inject(ReimbursementRequestService);
    reimbursementRequest$ = this.reimbursementRequestService.state$.pipe(
        tap((request) => {
            if (request) this.stipendRequestService.clearState();
            this.requestFormGroup.controls.paymentCard.markAsPristine();
        })
    );
    stipendRequestService = inject(StipendRequestService);
    stipendRequest$ = this.stipendRequestService.state$.pipe(
        tap((request) => {
            if (request) this.reimbursementRequestService.clearState();
        })
    );
    paymentRequestCard = viewChild<PaymentRequestCardComponent>(PaymentRequestCardComponent);

    travelCardEditable$ = this.request$.pipe(
        filter(Boolean),
        map((request) => {
            if (!this.isUpdate) return false;
            if (!request.status) return false;

            if (this.elevatedAccess) return true;

            const crcStatuses = [TravelRequestStatus.Draft, TravelRequestStatus.Submitted];
            return crcStatuses.includes(request.status.statusId);
        }),
        shareReplay(1)
    );
}
